import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

// Compute CSS Selectors Level 4 specificity — https://www.w3.org/TR/selectors-4/#specificity-rules
// Returns { a, b, c } where a = IDs, b = classes/attrs/pseudo-classes, c = types/pseudo-elements.
// Handles :where() (contributes 0), :is()/:not()/:has() (specificity of the most specific
// selector in the argument), and :nth-child(An+B of S)/:nth-last-child(An+B of S).

const EXAMPLE = "#main nav a.active, ul li:hover, :is(header, footer) .btn";

const IDENT = /[A-Za-z_\u00A0-\uFFFF-][\w\u00A0-\uFFFF-]*/y;

const LEGACY_PSEUDO_ELEMENTS = new Set([
  "before",
  "after",
  "first-line",
  "first-letter",
]);

const cmpSpec = (x, y) => {
  if (x.a !== y.a) return x.a - y.a;
  if (x.b !== y.b) return x.b - y.b;
  return x.c - y.c;
};

const splitTopLevel = (str, sep) => {
  const out = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < str.length; i += 1) {
    const ch = str[i];
    if (ch === "(" || ch === "[") depth += 1;
    else if (ch === ")" || ch === "]") depth -= 1;
    else if (depth === 0 && ch === sep) {
      out.push(str.slice(start, i));
      start = i + 1;
    }
  }
  out.push(str.slice(start));
  return out.map((s) => s.trim()).filter((s) => s.length > 0);
};

const maxOf = (list) => {
  let best = { a: 0, b: 0, c: 0 };
  for (const part of list) {
    const sp = calcSpecificity(part);
    if (cmpSpec(sp, best) > 0) best = sp;
  }
  return best;
};

function calcSpecificity(selector) {
  let a = 0;
  let b = 0;
  let c = 0;
  const s = String(selector || "");
  let i = 0;

  const readIdent = () => {
    IDENT.lastIndex = i;
    const m = IDENT.exec(s);
    if (!m) return null;
    i = IDENT.lastIndex;
    return m[0];
  };

  while (i < s.length) {
    const ch = s[i];

    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i += 1;
      continue;
    }
    if (ch === ">" || ch === "+" || ch === "~" || ch === "|") {
      i += 1;
      continue;
    }
    if (ch === "*") {
      i += 1;
      continue;
    }
    if (ch === "&") {
      i += 1;
      continue;
    }
    if (ch === "#") {
      i += 1;
      const name = readIdent();
      if (name) a += 1;
      continue;
    }
    if (ch === ".") {
      i += 1;
      const name = readIdent();
      if (name) b += 1;
      continue;
    }
    if (ch === "[") {
      let depth = 0;
      while (i < s.length) {
        if (s[i] === "[") depth += 1;
        else if (s[i] === "]") {
          depth -= 1;
          if (depth === 0) {
            i += 1;
            break;
          }
        }
        i += 1;
      }
      b += 1;
      continue;
    }
    if (ch === ":") {
      i += 1;
      const isDoubleColon = s[i] === ":";
      if (isDoubleColon) i += 1;
      const name = readIdent();
      if (!name) continue;
      const lname = name.toLowerCase();
      const treatAsPseudoElement =
        isDoubleColon || LEGACY_PSEUDO_ELEMENTS.has(lname);

      // Functional pseudo: read balanced parenthesis body.
      if (s[i] === "(") {
        let depth = 0;
        const argStart = i + 1;
        while (i < s.length) {
          if (s[i] === "(") depth += 1;
          else if (s[i] === ")") {
            depth -= 1;
            if (depth === 0) break;
          }
          i += 1;
        }
        const args = s.slice(argStart, i);
        if (s[i] === ")") i += 1;

        if (lname === "where") {
          // Contributes 0 to specificity.
          continue;
        }
        if (lname === "is" || lname === "not" || lname === "has") {
          const parts = splitTopLevel(args, ",");
          const best = maxOf(parts);
          a += best.a;
          b += best.b;
          c += best.c;
          continue;
        }
        if (lname === "nth-child" || lname === "nth-last-child") {
          b += 1;
          const m = /\bof\s+([\s\S]+)$/i.exec(args);
          if (m) {
            const parts = splitTopLevel(m[1], ",");
            const best = maxOf(parts);
            a += best.a;
            b += best.b;
            c += best.c;
          }
          continue;
        }
        // Any other functional pseudo: treat by its class (element vs class).
        if (treatAsPseudoElement) c += 1;
        else b += 1;
        continue;
      }

      if (treatAsPseudoElement) c += 1;
      else b += 1;
      continue;
    }
    // Type / element selector.
    if (/[A-Za-z_]/.test(ch)) {
      const name = readIdent();
      if (name) c += 1;
      continue;
    }
    // Unknown character — skip to avoid infinite loop.
    i += 1;
  }

  return { a, b, c };
}

const formatSpec = ({ a, b, c }) => `${a},${b},${c}`;

const CssSpecificityCalculator = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(EXAMPLE);

  const analysis = useMemo(() => {
    const trimmed = input.trim();
    if (!trimmed) return { empty: true, selectors: [] };
    const parts = splitTopLevel(trimmed, ",");
    if (parts.length === 0) return { empty: true, selectors: [] };
    const selectors = parts.map((raw) => ({
      raw,
      spec: calcSpecificity(raw),
    }));
    let winnerIndex = 0;
    let ties = false;
    for (let i = 1; i < selectors.length; i += 1) {
      const cmp = cmpSpec(selectors[i].spec, selectors[winnerIndex].spec);
      if (cmp > 0) {
        winnerIndex = i;
        ties = false;
      } else if (cmp === 0) {
        ties = true;
      }
    }
    return {
      empty: false,
      selectors,
      winnerIndex,
      ties,
    };
  }, [input]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const loadExample = () => setInput(EXAMPLE);
  const clearAll = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("css_spec_title")}
            </h1>
            <p className="text-gray-600">{t("css_spec_intro")}</p>

            <div className="flex flex-wrap gap-2">
              <StandardButton
                onClick={loadExample}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("css_spec_load_example")}
              </StandardButton>
              <StandardButton
                onClick={clearAll}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("css_spec_clear")}
              </StandardButton>
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="css-spec-input"
                className="text-sm font-medium text-gray-700"
              >
                {t("css_spec_input_label")}
              </label>
              <textarea
                id="css-spec-input"
                rows={3}
                spellCheck={false}
                autoComplete="off"
                className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder={t("css_spec_input_placeholder")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                {t("css_spec_input_hint")}
              </p>
            </div>

            {analysis.empty ? (
              <p className="text-sm text-gray-500 italic">
                {t("css_spec_empty_hint")}
              </p>
            ) : (
              <div
                role="status"
                aria-live="polite"
                className="flex flex-col gap-3"
              >
                {analysis.selectors.map((sel, idx) => {
                  const isWinner =
                    !analysis.ties &&
                    analysis.selectors.length > 1 &&
                    idx === analysis.winnerIndex;
                  const cardClass = isWinner
                    ? "bg-green-50 border-green-300"
                    : "bg-gray-50 border-gray-200";
                  return (
                    <div
                      key={`${idx}-${sel.raw}`}
                      className={`border rounded-md p-3 ${cardClass}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-mono text-sm break-all">
                          {sel.raw}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-mono text-sm px-2 py-1 rounded bg-indigo-100 text-indigo-800 border border-indigo-200"
                            aria-label={t("css_spec_specificity_aria", {
                              value: formatSpec(sel.spec),
                            })}
                          >
                            {formatSpec(sel.spec)}
                          </span>
                          <StandardButton
                            onClick={() =>
                              copy(formatSpec(sel.spec), t("css_spec_value_label"))
                            }
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {t("copy_button")}
                          </StandardButton>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                        <div className="bg-white border border-indigo-200 rounded-md p-2">
                          <div className="text-xs uppercase tracking-wide text-indigo-700">
                            {t("css_spec_part_a")}
                          </div>
                          <div className="font-mono">{sel.spec.a}</div>
                          <div className="text-xs text-gray-500">
                            {t("css_spec_part_a_hint")}
                          </div>
                        </div>
                        <div className="bg-white border border-indigo-200 rounded-md p-2">
                          <div className="text-xs uppercase tracking-wide text-indigo-700">
                            {t("css_spec_part_b")}
                          </div>
                          <div className="font-mono">{sel.spec.b}</div>
                          <div className="text-xs text-gray-500">
                            {t("css_spec_part_b_hint")}
                          </div>
                        </div>
                        <div className="bg-white border border-indigo-200 rounded-md p-2">
                          <div className="text-xs uppercase tracking-wide text-indigo-700">
                            {t("css_spec_part_c")}
                          </div>
                          <div className="font-mono">{sel.spec.c}</div>
                          <div className="text-xs text-gray-500">
                            {t("css_spec_part_c_hint")}
                          </div>
                        </div>
                      </div>
                      {isWinner && (
                        <div className="mt-2 text-xs text-green-800">
                          {t("css_spec_winner")}
                        </div>
                      )}
                    </div>
                  );
                })}
                {analysis.selectors.length > 1 && analysis.ties && (
                  <div className="text-xs text-gray-600 italic">
                    {t("css_spec_tie")}
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 mt-2 text-xs text-gray-500 leading-relaxed">
              <p>{t("css_spec_note_spec")}</p>
              <p>{t("css_spec_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CssSpecificityCalculator;
